import { groq } from "next-sanity";
import {
  BLOG_TYPE,
  COOKIES_TYPE,
  EVENT_TYPE,
  HOMEPAGE_TYPE,
  NEWS_EVENTS_OVERVIEW_TYPE,
  NEWS_TYPE,
  PAGE_NOT_FOUND_TYPE,
  PERSON_OVERVIEW_TYPE,
  PERSON_TYPE,
  PROJECT_OVERVIEW_TYPE,
} from "clariah/utils/constants";
import { EN, NL, PROJECT_TYPE, SEARCH_TYPE } from "shared/utils/constants";
import { createInternalHref } from "clariah/utils/createInternalHref";
import { fetchQuery, fetchPathsForTypes, localize } from "clariah/utils/sanity";
import { undefinedToNull } from "shared/utils/undefinedToNull";
import { getYesterdayISOString } from "shared/utils/formatDate";

export const getStaticPathsForTypes = async ({ locales, types }) => {
  const paths = await fetchPathsForTypes({ locales, types });
  return { paths, fallback: true };
};

export const navigationQuery = groq`*[_type == "clariah.navigation"] {
  items[] {
    ...,
    item-> {
      _type,
      slug,
      type,
      scope
    },
    subitems[] {
      ...,
      item-> {
        _type,
        slug,
        type,
        scope
      }
    }
  }
}[0].items`;

export const footerQuery = groq`*[_type == "clariah.footer"] {
  ...,
  footerMenu[] {
    ...,
    item-> {
      _type,
      slug,
      type,
      scope
    }
  },
  menus[] {
    ...,
    items[] {
      ...,
      item-> {
        _type,
        slug,
        type,
        scope
      }
    }
  }
}[0]`;

export const slugQuery = (locale, filter = "") => groq`*[
  (slug.${locale}.current == $slug || slug.current == $slug)
  && _type in $types
  ${filter}
]{
  "navigation": ${navigationQuery},
  "footer": ${footerQuery},
  "mirrorPath": coalesce(slug.${locale === NL ? EN : NL}.current, slug.current),
  ...,
  seo {
    ...,
    "image": image.asset->url
  },
  heroImage {
    ...,
    "url": image.asset->url
  },
  "image": image.asset->,
  "related": related[]->{
    heroImage {
      ...,
      "url": image.asset->url
    },
    scope,
    _type,
    date,
    endDate,
    intro,
    publishedAt,
    slug,
    title,
  },
  "items": items[]->{
    _type,
    heroImage {
      ...,
      "url": image.asset->url
    },
    intro,
    slug,
    title,
    date,
    endDate,
    scope
  },
  date,
  location,
  details {
    ...,
    button {
      ${locale} {
      ...,
      page -> { _type, slug, scope }
      }
    },
  },
  author -> {
    ...,
    "image": image.asset->,
    titles[] {
      "title": title,
      "organization": organization->name
    }
  },
  resourceTypes[]->{ title, slug },
  researchDomains[]->{ title, slug },
  researchActivities[]->{ title, slug },
  disciplines[]->{ name, slug },
  specialism[]->{ name, slug },
  partners[]->{ name, website },
  researchers[]->{
    _type,
    "image": image.asset->,
    name,
    website,
    slug,
    titles[] {
      "title": title,
      "organization": organization->name
    }
  },
  "publications": publications[].publication,
  titles[] {
    "title": title,
    "organization": organization->name
  },
  body{
    ...,
    ${locale}[]{
      ...,
      _type == "customImageWithDetails" => {
        ...,
        "image": image.asset->
      },
      _type == "quote" => {
        ...,
        "image": image.asset->
      },
      _type == "contactCard" => {
        ...,
        "image": image.asset->
      },
      _type == "contactPersonCard" => {
        ...,
        person -> {
          ...,
          "image": image.asset->,
          titles[] {
            "title": title,
            "organization": organization->name
          },
        }
      },
      _type == "buttons" => {
        ...,
        buttons[] {
          ...,
          page -> { _type, slug, scope }
        }
      },
      _type == "strongLinks" => {
        ...,
        buttons[] {
          ...,
          page -> { _type, slug, scope }
        }
      },
    }
  },
  heroList,
  heroListTitle,
  offsetImage {
    ...,
    ${locale} {
      ...,
      button {
        ...,
        page -> { _type, slug, scope }
      },
      "image": {
        "alt": image.alt,
        "url": image.image.asset->url
      }
    }
  },
  quote {
    ...,
    ${locale} {
      ...,
      cite {
        ...,
        image {
          "alt": alt,
          "url": image.asset->url
        }
      }
    }
  },
  cards {
    ...,
    ${locale} {
      ...,
      "items": items[]->{
        _type,

        heroImage {
          ...,
          "url": image.asset->url
        },
        intro,
        slug,
        title,
        date,
        endDate,
        scope,
      }
    }
  },
  logos {
    ...,
    ${locale} {
      ...,
      items[] -> {
        name,
        "url": logo.asset -> url
      },
      button {
        ...,
        page -> { _type, slug, scope }
      }
    }
  },
  cta {
    ...,
    ${locale} {
      ...,
      button {
        ...,
        page -> { _type, slug, scope }
      },
    }
  },
}[0]`;

export const overviewQuery = (filter = "") =>
  groq`*[_type == $overviewType]{
    "navigation": ${navigationQuery},
    "footer": ${footerQuery},
    seo { ..., "image": image.asset->url }, 
    title, 
    intro, 
    departments[]->,
    "posts": *[_type == $postType && !(_id in path("drafts.**")) ${filter}]{
      _type,
      slug,
      seo {
        ...,
        "image": image.asset->url
      },
      "overviewImage": overviewImage.asset->url,
      heroImage {
        ...,
        "url": image.asset->url
      },
      title,
      name,
      "intro": coalesce(summary, intro),
      date,
      endDate,
      "image": image.asset->,
      publishedAt,
      isFeatured,
      categories[]->{ name, intro, slug },
      departments[]-> { name, slug },
      researchDomains[]->{ title, slug },
      researchActivities[]->{ title, slug },
      resourceTypes[]->{ title, slug },
      titles[] {
        "title": title,
        "organization": organization->name
      },
    } | order(publishedAt desc) | order(date asc) | order(endDate asc) | order(_createdAt desc)
  }[0]`;

export const newsFilter = `&& publishedAt < now() && (!defined(expiresAt) || expiresAt > now())`;

export const eventsFilter = `&& (date >= "${getYesterdayISOString()}" || endDate >= "${getYesterdayISOString()}") && (!defined(expiresAt) || expiresAt > now())`;

export const peopleFilter = "&& defined(departments)";

export const projectFilter = "&& scope.clariah";

const postFields = `_id,
  _type,
  slug,
  "overviewImage": overviewImage.asset->url,
  heroImage {
    ...,
    "url": image.asset->url
  },
  title,
  "intro": coalesce(summary, intro),
  date,
  endDate,
  publishedAt`;

export const newsEventsOverviewQuery = `*[_type == $overviewType]{
    "navigation": ${navigationQuery},
    "footer": ${footerQuery},
    seo { ..., "image": image.asset->url }, 
    title, 
    intro, 
    projectCategories[]->{ slug },
    "featured": *[_type in [$blogType, $newsType, $eventType] && isFeatured == true] | order(publishedAt desc) | order(date desc) {
      ${postFields},
      "featured": isFeatured,
    }[0],
  }
  {
    ...,
    "blogs": *[_type == $blogType && _id != ^.featured._id && !(_id in path("drafts.**")) && publishedAt <= now()] | order(publishedAt asc) {
      ${postFields}
    }[0...3],
    "events": *[_type == $eventType && _id != ^.featured._id && !(_id in path("drafts.**")) && (date >= $yesterday || endDate >= $yesterday) && (!defined(expiresAt) || expiresAt > now())] | order(date asc) {
      ${postFields}
    }[0...3],
    "news": *[_type == $newsType && _id != ^.featured._id && !(_id in path("drafts.**")) && publishedAt < now() && (!defined(expiresAt) || expiresAt > now())] | order(publishedAt desc) | order(_createdAt desc) {
      ${postFields}
    }[0...3],
  }[0]`;

export const getPropsForSearch = async ({ locale, preview = false }) => {
  const data = await fetchQuery({
    preview,
    query: `{
      "navigation": ${navigationQuery},
      "footer": ${footerQuery},
    }`,
  });

  const mirrorPath = createInternalHref({
    href: "",
    locale: locale === NL ? EN : NL,
    type: SEARCH_TYPE,
  });

  return {
    props: {
      preview,
      mirrorPath,
      ...undefinedToNull(localize(data, locale)),
    },
  };
};

export const getPropsForCookiesPage = async ({ locale, preview = false }) => {
  const data = await fetchQuery({
    preview,
    params: {
      pageType: COOKIES_TYPE,
    },
    query: `*[_type == $pageType]{
      "navigation": ${navigationQuery},
      "footer": ${footerQuery},
      ...,
      seo {
        ...,
        "image": image.asset->url
      },
      heroImage {
        ...,
        "url": image.asset->url
      },
      body {
        ...,
        ${locale}[]{
          ...,
          _type == "customImageWithDetails" => {
            ...,
            "image": image.asset->
          },
          _type == "quote" => {
            ...,
            "image": image.asset->
          },
          _type == "contactCard" => {
            ...,
            "image": image.asset->
          },
          _type == "contactPersonCard" => {
            ...,
            person -> {
              ...,
              "image": image.asset->,
              titles[] {
                "title": title,
                "organization": organization->name
              },
            }
          },
          _type == "buttons" => {
            ...,
            buttons[] {
              ...,
              page -> { _type, slug, scope }
            }
          },
          _type == "strongLinks" => {
            ...,
            buttons[] {
              ...,
              page -> { _type, slug, scope }
            }
          },
        }
      },
    }[0]`,
  });

  const mirrorPath = createInternalHref({
    href: "",
    locale: locale === NL ? EN : NL,
    type: COOKIES_TYPE,
  });

  return {
    props: {
      preview,
      mirrorPath,
      ...undefinedToNull(localize(data, locale)),
    },
  };
};

export const getPropsForHomepage = async ({ locale, preview = false }) => {
  const data = await fetchQuery({
    preview,
    params: {
      pageType: HOMEPAGE_TYPE,
    },
    query: `*[_type == $pageType]{
      "navigation": ${navigationQuery},
      "footer": ${footerQuery},
      seo {
        ...,
        "image": image.asset->url
      },
      hero {
        image {
          alt,
          "url": image.asset->url
        },
        intro,
        links[] -> {
          title,
          slug
        },
        video {
          ${locale} {
            id,
            type
          }
        },
        highlight {
          ...,
          thumbnail {
            alt,
            "url": image.asset->url
          },
          href {
            ${locale} {
              ref->{
                _type,
                slug,
              },
              url
            }
          }
        }
      },
      about {
        ...,
        sections[] {
          ...,
          "image": {
          	"alt": image.alt,
          	"url": image.image.asset->url
        	},
          page -> {
            slug,
            heroList
          }
        }
      },
      ineo {
        ...,
        ${locale} {
          ...,
          button {
            ...,
            page -> { _type, slug, scope }
          },
          "image": {
            "alt": image.alt,
            "url": image.image.asset->url
          }
        }
      },
      quote {
        ${locale} {
          ...,
          cite {
            ...,
            image {
              "alt": alt,
              "url": image.asset->url
            }
          }
        },
      },
      aboutResources {
        ${locale} {
          title,
          body,
          button {
            ...,
            page -> { _type, slug, scope }
          },
        },
      },
      cards {
        ${locale} {
          title,
          "items": items[]->{
            ...,
            "intro": coalesce(summary, intro),
            "overviewImage": overviewImage.asset->url,
            heroImage {
              ...,
              "url": image.asset->url
            },
          }
        },
      },
      partners {
        ${locale} {
          title,
          link,
          items[] -> {
            name,
            "url": logo.asset -> url
          },
          button {
            ...,
            page -> { _type, slug, scope }
          }
        },
      },
    }[0]`,
  });

  const mirrorPath = createInternalHref({
    href: "",
    locale: locale === NL ? EN : NL,
    type: HOMEPAGE_TYPE,
  });

  return {
    props: {
      preview,
      mirrorPath,
      ...undefinedToNull(localize(data, locale)),
    },
  };
};

export const getPropsFor404 = async ({ preview = false, locale }) => {
  const data = await fetchQuery({
    preview,
    params: {
      pageType: PAGE_NOT_FOUND_TYPE,
    },
    query: `*[_type == $pageType]{
      "navigation": ${navigationQuery},
      "footer": ${footerQuery},
      ...,
      button {
        ...,
        page -> { _type, slug, scope }
      }
    }[0]`,
  });

  return {
    props: {
      preview,
      ...undefinedToNull(localize(data, locale)),
    },
  };
};
